import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import type { ScenarioDetail } from "@/lib/scenarios";

type MaterialCollectedProps = {
  scenario: ScenarioDetail;
  onTakeCheckpoint: () => void;
  onLater: () => void;
};

/**
 * Bridge screen between completing all session steps and the
 * checkpoint. A celebratory hero with the scenario palette gradient,
 * the "X слов / Y правил / Z фраз" stat row, and a primary CTA to
 * the checkpoint.
 *
 * "Позже" leaves status at READY_FOR_CHECKPOINT; the home screen's
 * Continue card morphs to the gradient checkpoint variant.
 *
 * Mockup: `design/hifi/scenarios-extra.jsx` `MaterialCollectedScreen`
 * lines ~526–581.
 */
export function MaterialCollectedScreen({ scenario, onTakeCheckpoint, onLater }: MaterialCollectedProps) {
  const t = useT();
  return (
    <div
      className="flex min-h-[calc(100dvh-40px)] flex-col rounded-[20px]"
      style={{
        background: "linear-gradient(180deg, var(--scen-bg) 0%, var(--scen-bg2) 100%)",
      }}
    >
      <div className="flex justify-end p-3.5">
        <button
          type="button"
          aria-label="close"
          onClick={onLater}
          className="rounded-md p-2 text-text-2 transition-colors hover:bg-white/40 hover:text-text"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="px-6 pt-3 pb-2">
        <div
          className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider"
          style={{ color: "var(--scen-fg)", backdropFilter: "blur(8px)" }}
        >
          <Check className="size-3" />
          {t("skills.material_title")}
        </div>
        <h1 className="text-[36px] font-bold leading-[1.1] tracking-[-0.03em]" style={{ color: "var(--scen-ink)" }}>
          {scenario.title}
        </h1>
        <p className="mt-3 text-[14px] leading-[1.5]" style={{ color: "var(--scen-fg)" }}>
          {t("skills.material_subtitle", { title: scenario.title })}
        </p>
      </div>

      <div className="px-6 pt-5">
        <div
          className="grid grid-cols-3 gap-1 rounded-[var(--r-md)] bg-white/72 px-4 py-3.5"
          style={{ backdropFilter: "blur(10px)" }}
        >
          <Stat n={scenario.wordCount} label={t("skills.material_words_collected")} />
          <Stat n={scenario.ruleCount} label={t("skills.material_rules_collected")} />
          <Stat n={scenario.constructionCount} label={t("skills.material_lines_collected")} />
        </div>
      </div>

      <div className="flex-1" />

      <div className="flex flex-col gap-2 rounded-t-[20px] border-t border-border bg-surface px-5 py-4">
        <Button type="button" variant="scenario" size="block" onClick={onTakeCheckpoint}>
          <Check />
          {t("skills.material_take_checkpoint", {
            mins: String(Math.max(3, Math.round(scenario.estimatedMinutes / 3))),
          })}
        </Button>
        <button
          type="button"
          onClick={onLater}
          className="w-full rounded-[var(--r-md)] py-2.5 text-[13px] text-text-3 transition-colors hover:text-text"
        >
          {t("skills.material_later")}
        </button>
      </div>
    </div>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="text-center">
      <div
        className="inline-flex items-center gap-1 text-[22px] font-bold tracking-[-0.02em]"
        style={{ color: "var(--scen-ink)" }}
      >
        <Check className="size-3" style={{ color: "var(--scen-fg)" }} />
        {n}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--scen-fg)" }}>
        {label}
      </div>
    </div>
  );
}
